/* eslint-disable camelcase */
import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import omit from 'lodash/omit';

import { required } from '../helpers/formValidators';
import api from '../api';

import Modal from './Modal';
import ProfileDeleteModal from './ProfileDeleteModal';
import {
  FormIconChooser,
  FormFeedback,
  FormInput,
  FormLabel,
  FormRadio,
  FormSelect,
  FormCheckbox,
  BaseForm
} from './form';
import { PrimaryButton, SecondaryButton } from './buttons';
import profileIcons from './icons/profileIcons';
import ErrorMessage from './ErrorMessage/ErrorMessage';
import { getProfiles } from '../selectors/profiles';
import { getAuthenticatedAccountId } from '../selectors/auth';

import './ProfileEditorModal.scss';

// Move default parameters into the signature
const ProfileEditorModal = ({
  isEditingCurrentProfile,
  isErrorMessageVisible,
  isLoading,
  isOpen,
  isPrimary,
  isRoleOtherSelected,
  t,
  initialValues = null,
  onRequestClose,
  updateProfile,
  accountId,
  createProfile,
  roles: rolesProp,
  onDelete,
  ...props
}) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [uniqueName, setUniqueName] = useState(true);

  const isNew = !initialValues;
  const profileId = initialValues?.id || null;

  // Memoize icons once
  const iconsForChooser = useMemo(
    () =>
      Object.keys(profileIcons).map(icon => (
        <FormRadio
          key={icon}
          name="icon"
          value={icon}
          imageSrc={profileIcons[icon]}
          disabled={isLoading}
          initialValue={initialValues?.icon}
        />
      )),
    [isLoading, initialValues?.icon]
  );

  const openDeleteModal = () => !isLoading && setIsDeleteModalOpen(true);
  const closeDeleteModal = () => !isLoading && setIsDeleteModalOpen(false);

  useEffect(() => {
    if (isOpen) setUniqueName(true);
  }, [isOpen]);

  const roles = useMemo(
    () =>
      rolesProp.map(role => ({
        value: role.id,
        label: role.other
          ? `${t(`roles.${role.name}`)} ${t('profile.roleOtherFillInBelow')}`
          : t(`roles.${role.name}`)
      })),
    [rolesProp, t]
  );

  const handleSubmit = (values) => {
    const commonBody = {
      ...values,
      status: 'active'
    };

    const action = profileId 
      ? updateProfile({ accountId, profileId }, { 
          body: JSON.stringify({
            ...omit(values, 'roleId'),
            roleId: values.roleId?.value || values.roleId,
            status: 'active'
          }) 
        })
      : createProfile({ accountId }, { body: JSON.stringify(commonBody) });

    action
      .then((data) => data && onRequestClose())
      .catch(() => <ErrorMessage />); // Note: ErrorMessage should ideally be a state-driven UI piece
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      className="profile-editor-modal"
      showCloseButton={!isLoading}
      shouldCloseOnOverlayClick={false}
    >
      <div className="profile-editor__header">
        <h3>
          {isNew ? t('profile.addProfileTitle') : t('profile.editProfileTitle')}
        </h3>
      </div>

      <BaseForm
        className={classNames('profile-editor', {
          'profile-editor--new': isNew,
          'profile-editor--primary': isPrimary
        })}
        onSubmit={handleSubmit}
        name="profileEditor"
      >
        <div className="profile-editor__fields">
          <div className="profile-editor__name-fields">
            <FormInput
              disabled={isLoading}
              labelRenderer={() => <FormLabel>{t('profile.firstNameLabel')}</FormLabel>}
              id="profile.firstName"
              name="firstName"
              placeholder={t('profile.firstNamePlaceholder')}
              type="text"
              initialValue={initialValues?.firstName}
              validate={required}
            />
            <FormInput
              labelRenderer={() => <FormLabel>{t('profile.lastNameLabel')}</FormLabel>}
              id="profile.lastName"
              name="lastName"
              placeholder={t('profile.lastNamePlaceholder')}
              type="text"
              initialValue={initialValues?.lastName}
              validate={required}
            />
          </div>
          
          <FormLabel>{t('profile.roleLabel')}</FormLabel>
          <FormSelect
            disabled={isLoading}
            id="profile.roleId"
            name="roleId"
            placeholder={t('profile.rolePlaceholder')}
            initialValue={initialValues?.roleId}
            options={roles}
            validate={required}
          />

          {isRoleOtherSelected && (
            <FormInput
              disabled={isLoading}
              id="profile.roleOther"
              name="roleOther"
              placeholder={t('profile.roleOtherPlaceholder')}
              type="text"
            />
          )}
        </div>

        <div className="profile-editor__icon-field">
          <FormLabel>{t('profile.iconLabel')}</FormLabel>
          <FormIconChooser>{iconsForChooser}</FormIconChooser>
        </div>

        {isErrorMessageVisible && (
          <FormFeedback positive={false}>
            {t('profile.formErrorMessage')}
          </FormFeedback>
        )}

        {!uniqueName && (
          <>
            <FormFeedback positive={false}>
              There is already a profile with that name
            </FormFeedback>
            <FormCheckbox name="forceCreate" value={false} required>
              Create Duplicate Profile Name
            </FormCheckbox>
          </>
        )}

        <div className="profile-editor__footer">
          {!isNew && !isPrimary && (
            <SecondaryButton
              className="profile-editor__delete"
              colorScheme="red"
              disabled={isLoading}
              type="button"
              onClick={openDeleteModal}
            >
              {t('profile.delete')}
            </SecondaryButton>
          )}
          <PrimaryButton
            className="profile-editor__save"
            colorScheme="blue"
            type="submit"
            loading={isLoading}
          >
            {t('profile.save')}
          </PrimaryButton>
        </div>
      </BaseForm>

      {!isNew && (
        <ProfileDeleteModal
          isOpen={isDeleteModalOpen}
          isLoading={isLoading}
          isCurrentProfile={isEditingCurrentProfile}
          onCancel={closeDeleteModal}
          onConfirm={onDelete}
          profileId={profileId}
        />
      )}
    </Modal>
  );
};

ProfileEditorModal.propTypes = {
  initialValues: PropTypes.object,
  isButtonDisabled: PropTypes.bool.isRequired,
  isEditingCurrentProfile: PropTypes.bool.isRequired,
  isErrorMessageVisible: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isOpen: PropTypes.bool.isRequired,
  isPrimary: PropTypes.bool.isRequired,
  isRoleOtherSelected: PropTypes.bool.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  roles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      other: PropTypes.bool
    })
  ).isRequired,
  t: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  profiles: getProfiles(state),
  accountId: getAuthenticatedAccountId(state)
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      updateProfile: api.actions.profilesPatch,
      createProfile: api.actions.profilesPost
    },
    dispatch
);

export default connect(mapStateToProps, mapDispatchToProps)(ProfileEditorModal);

